import React, { useState } from 'react';
import SurveyRecordsTable from '../SurveyRecordsTable';
import { EnhanceTableAction, IField, IPatient, ISubmission, ISurvey } from '../../../constants/types';
import { Translate } from 'react-localize-redux';
import { CATEGORY_DEPARTMENT_PRESCRIPTIONS } from '../../../constants';
import * as types from "../../../constants";
import Modal from '../../../components/general/modal';
import { Typography, TextField, Snackbar } from '@mui/material';
import { Alert } from "@mui/lab";
import { HOSPITAL_BILLING_EDIT } from '../../../routes/urls';
import { LinkStyled } from '../../../components/general/mini_components';
import { CaseStatusChip } from './CaseStatusChip';
import { Link } from 'react-router-dom';
import { BillingInfo } from '../Billing/types';
import SubmissionsTableSelector from './SubmissionsTableSelector';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import { SubmissionPDFDocument } from '../../../components/pdf/documents/SubmissionPDFDocument';
import { PDFDownloadButtonWithSignature } from '../../../components/pdf/components/PDFDownloadButtonWithSignature';
import { usePDFConfig } from '../../../hooks/usePDFConfig';
import { useCurrentInvestigation } from '../../../hooks';
import { useSelector } from 'react-redux';
import { extractPrescribingDoctor } from '../../../utils/doctorUtils';
import { reopenCaseService } from '../../../services';
import { ButtonCancel, ButtonContinue } from '../../../components/general/mini_components';
import { formatSubmissionDateTime } from '../../../utils/dateUtils';
import { useSubmissionCatalogueNames } from '../../../hooks/useIcd11CatalogueNames';

export { CaseStatusChip };

interface SubmissionsTableProps {
    locale:string,
    fieldsSurvey?: IField[],
    surveys: ISurvey[],
    records: ISubmission[],
    category: number,
    patient:IPatient,
    billingInfo: BillingInfo | null,
    casesEnabled?: boolean,
    onSelectSubmission: (index:number) => void;
}

const SubmissionsTable: React.FC<SubmissionsTableProps> = ({ locale, surveys, patient, billingInfo, category, fieldsSurvey, records,
                                                        casesEnabled = false, onSelectSubmission }) => {
    const [showModalPrintPDF, setShowModalPrintPDF] = useState(false);
    const [submissionPrint, setSubmissionPrint] = useState<ISubmission | null>(null);
    const [showReopenModal, setShowReopenModal] = useState(false);
    const [reopenReason, setReopenReason] = useState('');
    const [reopenError, setReopenError] = useState('');
    const [reopenSubmissionIndex, setReopenSubmissionIndex] = useState<number | null>(null);
    const [snackbar, setSnackbar] = useState<{show: boolean, severity: "success" | "error", message: string}>({show: false, severity: "success", message: ""});

    // SHER-1101: the PDF export action shows unless the study opted out.
    const { isPdfExportEnabled } = useCurrentInvestigation();
    // Get PDF config and investigation UUID for new PDF component
    const { config: pdfConfig } = usePDFConfig();
    const currentInvestigationUuid = useSelector((state: any) =>
        state.investigations.currentInvestigation?.uuid
    );
    // SKIN-612 — WHO names for the ICD-11 codes of the submission being printed that no pick names.
    const catalogueNames = useSubmissionCatalogueNames(
        currentInvestigationUuid,
        submissionPrint,
        submissionPrint ? surveys.find((survey) => survey.uuid === submissionPrint.uuidSurvey) : null,
    );

    function printSubmission(submission:ISubmission){
        setSubmissionPrint(submission);
        setShowModalPrintPDF(true);
    }

    function handleReopenClick(index: number){
        setReopenSubmissionIndex(index);
        setReopenReason('');
        setReopenError('');
        setShowReopenModal(true);
    }

    async function handleReopenConfirm(){
        if(!reopenReason.trim()){
            setReopenError('pages.hospital.cases.reopen_reason_required');
            return;
        }
        if(reopenSubmissionIndex === null || !currentInvestigationUuid) return;

        const record = records[reopenSubmissionIndex];
        try {
            await reopenCaseService(currentInvestigationUuid, (record as any).uuid || record.id, reopenReason);
            setShowReopenModal(false);
            setReopenSubmissionIndex(null);
            setReopenReason('');
            // Reload page to refresh data
            window.location.reload();
        } catch(err) {
            setSnackbar({show: true, severity: "error", message: "general.error"});
            setShowReopenModal(false);
        }
    }

    function renderReopenModal(){
        return (
            <Modal key="reopen-modal" medium open={showReopenModal} title="" closeModal={() => setShowReopenModal(false)}>
                <Typography variant="h6" gutterBottom>
                    <Translate id="pages.hospital.cases.reopen" />
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={<Translate id="pages.hospital.cases.reopen_reason" />}
                    value={reopenReason}
                    onChange={(e) => {
                        setReopenReason(e.target.value);
                        if(reopenError) setReopenError('');
                    }}
                    error={!!reopenError}
                    helperText={reopenError ? <Translate id={reopenError} /> : ''}
                    style={{marginBottom: '1rem'}}
                />
                <div style={{display: 'flex', gap: '0.5rem', justifyContent: 'flex-end'}}>
                    <ButtonCancel onClick={() => setShowReopenModal(false)}>
                        <Translate id="general.cancel" />
                    </ButtonCancel>
                    <ButtonContinue onClick={handleReopenConfirm}>
                        <Translate id="general.confirm" />
                    </ButtonContinue>
                </div>
            </Modal>
        );
    }

    function renderPDFModal(){
        const currentSurvey = surveys.find((survey) => survey.uuid === submissionPrint?.uuidSurvey);

        if(!pdfConfig){
            return(
                <Modal key="modal" medium open={showModalPrintPDF} title="" closeModal={() => setShowModalPrintPDF(false)}>
                    <Typography variant='body2'><Translate id="pages.hospital.patient.fill_billing_info" />, <Link to={HOSPITAL_BILLING_EDIT}><Translate id="general.here" /></Link></Typography>
                </Modal>
            )
        }
        else if(currentSurvey && submissionPrint){
            // Find prescribing doctor from the submission
            const prescribingDoctor = extractPrescribingDoctor(submissionPrint);

            return(
                <Modal key="modal" medium open={showModalPrintPDF} title="" closeModal={() => setShowModalPrintPDF(false)}>
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <PDFDownloadButtonWithSignature
                            uuidInvestigation={currentInvestigationUuid || ''}
                            uuidResearcher={prescribingDoctor?.uuid}
                            filename={`${currentSurvey.name}-${submissionPrint.id}`}
                            variant="contained"
                            size="large"
                            label={<Translate id="general.download_pdf" />}
                        >
                            {(signatureBase64) => (
                                <SubmissionPDFDocument
                                    submission={submissionPrint}
                                    currentSurvey={currentSurvey}
                                    patient={patient}
                                    prescribingDoctor={prescribingDoctor}
                                    config={pdfConfig}
                                    nameSurvey={currentSurvey.name}
                                    locale={locale}
                                    uuidInvestigation={currentInvestigationUuid || ''}
                                    signatureBase64={signatureBase64}
                                    catalogueNames={catalogueNames}
                                />
                            )}
                        </PDFDownloadButtonWithSignature>
                    </div>
                </Modal>
            )
        }

    }
    if(surveys.length > 0 && [types.CATEGORY_DEPARTMENT_NURSE, types.CATEGORY_DEPARTMENT_NURSE_FW].includes(surveys[0].category)){
        return <SubmissionsTableSelector surveys={surveys} records={records}
                    locale={locale} billingInfo={billingInfo}
                    onSelectSubmission={(index) => onSelectSubmission(index)}  />
    }

    let headCells = [{ id: "researcher", alignment: "left", label: <Translate id="hospital.doctor.label" />}, { id: "surveyName", alignment: "left", label: <Translate id="hospital.data-collection" />},
                        { id: "date", alignment: "left", label: "Date"},
                        ...(casesEnabled ? [{ id: "status", alignment: "left", label: <Translate id="pages.hospital.services.status" />}] : [])];
    let actions:EnhanceTableAction[] = [
        ...(isPdfExportEnabled() ? [{
            "type" : "pdf",
            "func" : (index:number) => printSubmission(records[index]),
            "check": (row: any) => {
                const record = records[row.id];
                return record && (record as any).type !== "stay";
            }
        }] : []),
        ...(casesEnabled ? [{
            "type" : "lock_open",
            "func" : (index:number) => handleReopenClick(index),
            "check": (row: any) => {
                return row.statusValue === 'SUBMITTED';
            }
        }] : [])
    ];
    return(
        <>
            {renderPDFModal()}
            {casesEnabled && renderReopenModal()}
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={snackbar.show}
                autoHideDuration={3000}
                onClose={() => setSnackbar({...snackbar, show: false})}>
                    {snackbar.message ?
                        <Alert onClose={() => setSnackbar({...snackbar, show: false})} severity={snackbar.severity}>
                            <Translate id={snackbar.message} />
                        </Alert> : undefined
                    }
            </Snackbar>
            <EnhancedTable noHeader noSelectable selectRow={(index:number) => onSelectSubmission(index)}
                rows={records.map((record, index) => {
                    const dateCreatedString = record.createdAt ? formatSubmissionDateTime(record.createdAt) : "Unsincronized";
                    const caseStatus = (record as any).status || 'DRAFT';
                    return({id : index, researcher : record.researcher, surveyName : record.surveyName, date : dateCreatedString,
                        ...(casesEnabled ? { status: <CaseStatusChip status={caseStatus} /> } : {}), statusValue: caseStatus, type: (record as any).type})
                })} headCells={headCells}
                    actions={actions} />
        </>
    );
};

export default SubmissionsTable;
